// src/components/Gacha/GachaHistory.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardHeader, Avatar,
  Alert, Pagination,
} from '@mui/material';
import api from '../../api/axios';
import dayjs from 'dayjs';
import SafeChip from '../Common/SafeChip';

const PAGE_SIZE = 10;
const FIXED_COLOR = '#4B5563';         // ◆ 눈 편한 진회색
const colorOf = () => FIXED_COLOR;     // 고정 색 반환

/* 글자 수에 따라 Avatar 폰트 크기를 결정 */
const getTierFontSize = (tierLabel) => {
  const len = `${tierLabel}`.length;
  if (len <= 2) return 14;
  if (len === 3) return 12;
  if (len === 4) return 8;
  if (len === 5) return 7;   // 2px↓
  if (len === 6) return 6;   // 또 2px↓
  return 6;                  // 7글자 이상 최소 6px
};

export default function GachaHistory() {
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  /* ───────── 데이터 로드 ───────── */
  useEffect(() => {
    api
      .get('/api/gacha/history/')
      .then((r) => setLogs(r.data.history || []))
      .catch(() => setError('히스토리를 불러오지 못했습니다.'));
  }, []);                     // ← ✅ useEffect 닫기

  const pageCount = Math.ceil(logs.length / PAGE_SIZE);
  const slice = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (error)
    return <Alert severity="error">{error}</Alert>;
  if (!logs.length)
    return <Typography align="center">📜 뽑기 내역이 없습니다.</Typography>;

  const titleCnt = logs.filter((l) => l.type === 'title').length;
  const itemCnt = logs.length - titleCnt;

  /* ───────── 렌더 ───────── */
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" mb={1}>
        📜 뽑기 히스토리
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        칭호 {titleCnt}개 · 아이템 {itemCnt}개
      </Typography>

      {slice.map((log) => (
        <Card
          key={log.id}
          variant="outlined"
          sx={{
            mb: 2,
            pt: '10px',
            pb: '10px',
            borderWidth: 2,
            borderColor: colorOf(),
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              transition: '.2s',
            },
          }}
        >
          <CardHeader
            avatar={
              <Avatar
                sx={{
                  bgcolor: colorOf(),
                  color: '#fff',
                  width: 36,
                  height: 36,
                  fontWeight: 700,
                  fontSize: getTierFontSize(log.tier),
                }}
              >
                {log.tier}
              </Avatar>
            }
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SafeChip
                  label={log.type === 'title' ? '칭호' : '아이템'}
                  color={log.type === 'title' ? 'primary' : 'info'}
                  size="small"
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  {log.item_name}
                </Typography>
              </Box>
            }
            subheader={dayjs(log.created_at).format('YYYY-MM-DD HH:mm')}
            subheaderTypographyProps={{ fontSize: 12 }}
            sx={{ p: 0, pl: 2, pr: 2 }}
          />

          {log.description && (
            <CardContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {log.description}
              </Typography>
            </CardContent>
          )}
        </Card>
      ))}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
